import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import Markdown from 'react-markdown';
import type { AppState } from "@strands/agent-transport";

/**
 * Infographic main view — renders the latest `render_infographic` tool
 * call full-size in the central column as a header (title + subtitle)
 * plus a vertical list of typed block cards.
 *
 * Source of truth: `state.infographic = {title, subtitle, blocks, updated_at}`
 * written by the backend `render_infographic` tool. ChatPage auto-opens
 * the view on `updated_at` bumps. Clicking a historical InfographicCard
 * plate calls `openMain('infographic', { callBlocks, callTitle, callSubtitle })`
 * to pin a specific past infographic; auto-open re-issues openMain
 * without props to return to "latest" semantics.
 *
 * Init-size correctness:
 *   ECharts captures its container's dimensions at init via
 *   `echarts.init(dom)`. The `.main-slot--split` ancestor runs a 0.2s
 *   `flex-grow: 0 → 1` keyframe (globals.css `mainSlotExpand`).
 *   Mounting ECharts mid-animation locks an intermediate width into
 *   its layout cache — labels collide, only a hover-triggered redraw
 *   recovers. Robust fix: defer chart mounts until the slot animation
 *   finishes via `Element.getAnimations().finished`.
 */

type Block =
  | { type: 'kpi_row'; items: KpiItem[] }
  | { type: 'chart'; option: EChartsOption; title?: string }
  | { type: 'heading'; text: string }
  | { type: 'text'; markdown: string };

interface KpiItem {
  label: string;
  value: string;
  hint?: string;
}

interface InfographicState {
  title?: string;
  subtitle?: string | null;
  blocks?: Block[];
  updated_at?: number;
}

interface Props {
  state: AppState;
  sessionId: string | null;
  onClose: () => void;
  callTitle?: string;
  callSubtitle?: string | null;
  callBlocks?: Block[];
}

export function InfographicView({
  state,
  callTitle,
  callSubtitle,
  callBlocks,
}: Props) {
  const live = (state.infographic as InfographicState | undefined) ?? {};
  const title = callTitle ?? live.title;
  const subtitle = callSubtitle ?? live.subtitle ?? null;
  const blocks: Block[] = callBlocks ?? live.blocks ?? [];

  const containerRef = useRef<HTMLDivElement | null>(null);

  const [ready, setReady] = useState(false);
  useLayoutEffect(() => {
    let cancelled = false;
    const finish = () => { if (!cancelled) setReady(true); };

    const slot = containerRef.current?.closest('.main-slot--split');
    type WithGetAnimations = Element & { getAnimations?: () => Animation[] };
    const getAnims = (slot as WithGetAnimations | null)?.getAnimations;

    if (!slot || typeof getAnims !== 'function') {
      finish();
      return;
    }
    const running = getAnims.call(slot).filter((a) => a.playState === 'running');
    if (running.length === 0) {
      finish();
      return;
    }
    Promise.all(running.map((a) => a.finished)).then(finish, finish);
    const fallback = setTimeout(finish, 400);
    return () => {
      cancelled = true;
      clearTimeout(fallback);
    };
  }, []);

  if (blocks.length === 0) {
    return (
      <div className="canvas-view">
        <div className="canvas-body">
          <div className="canvas-empty">
            Здесь будет инфографика. Попросите ассистента «построй
            дашборд по продажам» — и он появится тут.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="canvas-view">
      <div ref={containerRef} className="canvas-body infographic-body">
        {/* Inner wrapper is the PDF capture root (see exportInfographicPdf).
            We don't capture `.canvas-body` directly because it's the scroll
            container — html2canvas would clip at its viewport height. */}
        <div className="infographic-content" data-infographic-export-root>
          {(title || subtitle) && (
            <header className="infographic-header">
              {title && <h1 className="infographic-title">{title}</h1>}
              {subtitle && <div className="infographic-subtitle">{subtitle}</div>}
            </header>
          )}
          <div className="infographic-blocks">
            {blocks.map((block, i) => (
              <BlockRenderer key={i} block={block} ready={ready} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function BlockRenderer({ block, ready }: { block: Block; ready: boolean }) {
  switch (block.type) {
    case 'kpi_row':
      return <KpiRow items={block.items} />;
    case 'chart':
      return <ChartBlock title={block.title} option={block.option} ready={ready} />;
    case 'heading':
      return <h2 className="infographic-heading">{block.text}</h2>;
    case 'text':
      return (
        <div className="infographic-text canvas-md">
          <Markdown>{block.markdown}</Markdown>
        </div>
      );
    default:
      return null;
  }
}

function KpiRow({ items }: { items: KpiItem[] }) {
  if (!items?.length) return null;
  return (
    <div className="infographic-card infographic-kpi-row">
      {items.map((it, i) => (
        <div key={i} className="infographic-kpi">
          <div className="infographic-kpi-label">{it.label}</div>
          <div className="infographic-kpi-value">{it.value}</div>
          {it.hint && <div className="infographic-kpi-hint">{it.hint}</div>}
        </div>
      ))}
    </div>
  );
}

function ChartBlock({
  title,
  option,
  ready,
}: {
  title?: string;
  option: EChartsOption;
  ready: boolean;
}) {
  const chartRef = useRef<ReactECharts | null>(null);
  const elRef = useRef<HTMLDivElement | null>(null);

  // Resize the chart whenever its container changes size — window resize,
  // sidebar collapse, MainSlot expand/collapse. This works because
  // `.canvas-view` has `min-width: 0` (see globals.css), which lets the
  // view actually shrink in its row-flex parent instead of pinning to
  // the chart's min-content. Without that, RO would still fire here, but
  // ECharts would measure a stale-wide container.
  useEffect(() => {
    if (!ready || typeof ResizeObserver === 'undefined') return;
    const el = elRef.current;
    if (!el) return;
    let rafId: number | null = null;
    const resize = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        chartRef.current?.getEchartsInstance()?.resize();
      });
    };
    const ro = new ResizeObserver(resize);
    ro.observe(el);
    return () => {
      ro.disconnect();
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [ready]);

  return (
    <div className="infographic-card infographic-chart-card">
      {title && <div className="infographic-chart-title">{title}</div>}
      <div ref={elRef} className="infographic-chart-canvas">
        {ready && (
          <ReactECharts
            ref={(r) => { chartRef.current = r; }}
            option={option}
            style={{ height: '100%', width: '100%' }}
            notMerge
            lazyUpdate
          />
        )}
      </div>
    </div>
  );
}
